import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { EmptyState } from '@/components/ui/EmptyState'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Sheet } from '@/components/ui/Sheet'
import { OwnerDeleteButton } from '@/features/owner/OwnerDelete'
import { useProfile } from '@/auth/SessionProvider'
import { canWriteDepartment } from '@/auth/profile'
import { createSheet, listSheets } from '@/data/sheets'
import { formatDay } from '@/lib/format'
import { formatKwacha } from '@/lib/money'
import { lusakaToday } from '@/lib/periods'
import { ProofPhotos } from '@/features/proofs/ProofPhotos'
import { subscribeQueue } from '@/attachments/queue'

const newId = () => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `00000000-0000-4000-8000-${Date.now().toString(16).padStart(12, '0').slice(-12)}`)
const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')

export function SheetsPage({ deptId }: { deptId: string }) {
  const profile = useProfile()
  const canWrite = canWriteDepartment(profile, deptId)
  const qc = useQueryClient()
  const today = lusakaToday()
  const q = useQuery({ queryKey: ['sheets', deptId], queryFn: () => listSheets(deptId) })

  // A photo that finishes uploading changes the "No photo yet" badge.
  useEffect(() => subscribeQueue(() => void qc.invalidateQueries({ queryKey: ['sheets', deptId] })), [qc, deptId])

  const [open, setOpen] = useState(false)
  const [sheetOn, setSheetOn] = useState(today)
  const [label, setLabel] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState('')
  const busy = useRef(false)
  // One id per form-open: a retry whose first answer was lost finds the sheet it already made.
  const sheetId = useRef(newId())
  const createM = useMutation({ mutationFn: () => createSheet(deptId, { sheetOn, label, note }, sheetId.current) })

  const openForm = () => {
    setSheetOn(today); setLabel(''); setNote(''); setError(''); sheetId.current = newId(); setOpen(true)
  }
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (busy.current) return
    setError('')
    if (!sheetOn) return setError('Choose the date of the collection.')
    if (sheetOn > today) return setError('The date cannot be in the future.')
    busy.current = true
    try {
      await createM.mutateAsync()
      await qc.invalidateQueries({ queryKey: ['sheets', deptId] })
      setOpen(false)
    } catch (err) {
      setError(errText(err))
    } finally {
      busy.current = false
    }
  }

  const sheets = q.data ?? []
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-title font-semibold text-ink">Collection sheets</h2>
        <p className="mt-1 text-muted">
          The paper you write cash dues on during a collection. Take one photo of the sheet: it is the proof for every payment written on it. When you record a cash payment, choose the sheet it belongs to.
        </p>
      </div>
      <Card>
        <CardHeader className="flex items-center justify-between gap-3">
          <h3 className="font-semibold text-ink">Your sheets</h3>
          {canWrite && <Button size="lg" onClick={openForm}>New collection sheet</Button>}
        </CardHeader>
        <CardBody>
          {q.isPending ? (
            <SkeletonRows rows={4} />
          ) : q.isError ? (
            <div>
              <p role="alert" className="text-body text-bad">{errText(q.error)}</p>
              <Button variant="secondary" size="lg" className="mt-3" onClick={() => void q.refetch()}>Try again</Button>
            </div>
          ) : sheets.length === 0 ? (
            <EmptyState title="No collection sheets yet" description={canWrite ? 'Start one on the day you collect cash dues, then photograph the page.' : 'Nothing has been recorded.'} />
          ) : (
            <ul className="divide-y divide-line" aria-label="Collection sheets">
              {sheets.map((s) => (
                <li key={s.id} className="py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-ink">{formatDay(s.sheetOn)}{s.label ? ` · ${s.label}` : ''}</p>
                      {s.note && <p className="text-body text-muted">{s.note}</p>}
                      <p className="mt-1 text-body text-muted">
                        {s.paymentCount === 0 ? 'No payments on it yet' : `${s.paymentCount} ${s.paymentCount === 1 ? 'payment' : 'payments'}`}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="font-medium text-ink">{formatKwacha(s.totalNgwee)}</p>
                      {s.photoCount === 0 ? <Badge tone="warning">No photo yet</Badge> : <Badge tone="success">Photo saved</Badge>}
                    </div>
                  </div>
                  <ProofPhotos parent={{ kind: 'sheet', id: s.id }} departmentId={deptId} date={s.sheetOn} canAdd={canWrite} noun="collection sheet" />
                  <OwnerDeleteButton table="collection_sheet" id={s.id} what="collection sheet" wrap="mt-2 flex flex-wrap gap-2" />
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Sheet isOpen={open} onClose={() => setOpen(false)} title="New collection sheet">
        <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" noValidate>
          <Input label="Date of the collection" type="date" max={today} value={sheetOn} onChange={(e) => setSheetOn(e.target.value)} />
          <Input label="Name (optional)" placeholder="Sunday service" value={label} onChange={(e) => setLabel(e.target.value)} />
          <Textarea label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
          {error && <p role="alert" className="text-body text-bad">{error}</p>}
          <Button type="submit" size="lg" isLoading={createM.isPending}>{createM.isPending ? 'Saving...' : 'Save sheet'}</Button>
        </form>
      </Sheet>
    </div>
  )
}
